/**
 * Find substitute SKUs that were placed in different zones from their parent.
 *
 * For each SKU with non-empty `substituteIds`, resolve the recommended zone of
 * both the SKU and each substitute via their slot assignments. Emit one entry
 * per (sku, substitute) pair whose zones differ. To avoid duplicate (A,B)/(B,A)
 * pairs, only emit when `sku.id < substituteId` lexicographically.
 *
 * SKUs without a slot assignment (or whose substitute has none) are skipped.
 */
export function findSubstituteSeparations(skus, assignments, zones, locations) {
    const assignmentBySku = new Map(assignments.map((a) => [a.skuId, a]));
    const locationById = new Map(locations.map((l) => [l.id, l]));
    const knownZoneIds = new Set(zones.map((z) => z.id));
    const resolveZoneId = (skuId) => {
        const a = assignmentBySku.get(skuId);
        if (!a)
            return null;
        const loc = locationById.get(a.recommendedLocationId);
        if (!loc)
            return null;
        if (!knownZoneIds.has(loc.zoneId))
            return null;
        return loc.zoneId;
    };
    const separations = [];
    for (const sku of skus) {
        if (!sku.substituteIds || sku.substituteIds.length === 0)
            continue;
        const skuZoneId = resolveZoneId(sku.id);
        if (!skuZoneId)
            continue;
        for (const subId of sku.substituteIds) {
            // Dedupe symmetric pairs: only emit when sku.id < subId
            if (sku.id >= subId)
                continue;
            const subZoneId = resolveZoneId(subId);
            if (!subZoneId)
                continue;
            if (skuZoneId !== subZoneId) {
                separations.push({ skuId: sku.id, substituteId: subId, skuZoneId, subZoneId });
            }
        }
    }
    return separations;
}
/** Resolve a zone display name from its id (falls back to the id). */
export function zoneName(zones, zoneId) {
    return zones.find((z) => z.id === zoneId)?.name ?? zoneId;
}
//# sourceMappingURL=substituteValidator.js.map